import { useMyAccess } from '@/lib/useMyAccess';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GroupedLayout } from '@/components/layout/GroupedLayout';
import { api } from '@/lib/api';
import { bursarGroups } from './nav';
import { StaffMessagesPage } from '@/features/shared/support/StaffMessagesPage';
import { SupportCenter } from '@/features/shared/support/SupportCenter';
import { DashboardPage } from './pages/DashboardPage';
import { FeeStructurePage } from './pages/FeeStructurePage';
import { DebtorsPage } from './pages/DebtorsPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { RecordPaymentPage } from './pages/RecordPaymentPage';
import { ReceiptsPage } from './pages/ReceiptsPage';
import { ReconciliationPage } from './pages/ReconciliationPage';
import { ReportsPage } from './pages/ReportsPage';
import { StockPage } from './pages/StockPage';
import { FeesPage } from '@/features/owner/pages/FeesPage';
import { OwnerWalletPage } from '@/features/owner/pages/OwnerWalletPage';
import { ExpensesPage } from '@/features/owner/pages/ExpensesPage';
import { PayrollPage } from '@/features/owner/pages/PayrollPage';
import { BooksPage } from '@/features/owner/pages/BooksPage';
import { NoticesPage } from '@/features/owner/pages/NoticesPage';
import { ApprovalsPage } from '@/features/shared/finance/ApprovalsPage';
import { AuditTrailPage } from '@/features/shared/support/AuditTrailPage';
import { ClockInPage } from '@/features/teacher/pages/ClockInPage';
import { MyPayPage } from '@/features/teacher/pages/MyPayPage';
import { AccountPage } from '@/features/auth/pages/AccountPage';
import { NotificationsCenter } from '@/features/auth/pages/NotificationsCenter';

/** The bursar's shell: the owner's dropdown menu, with live counts and the pages the owner switched off removed. */
function BursarLayout() {
  const d = useQuery({ queryKey: ['owner-dashboard'], queryFn: () => api.get('/school/dashboard').then((r) => r.data?.data), refetchInterval: 120_000, retry: false });
  const { hidden, customRole } = useMyAccess();
  const p = d.data?.pending ?? {};
  return (
    <GroupedLayout base="/bursar" roleLabel={customRole ?? "Bursar"} groups={bursarGroups} padded hidden={hidden}
      ownPadding={['wallet', 'bank-accounts', 'withdrawals', 'expenses', 'payroll', 'books', 'approvals', 'notices']}
      badges={{ approvals: p.money_requests, withdrawals: p.withdrawals, expenses: p.expenses, 'bank-accounts': p.bank_accounts }} />
  );
}

export function bursarRoutes() {
  return (
    <Routes>
      <Route element={<BursarLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />

        <Route path="fee-structure" element={<FeesPage initialTab="structures" />} />
        <Route path="fee-heads" element={<FeesPage initialTab="heads" />} />
        <Route path="discounts" element={<FeeStructurePage />} />
        <Route path="invoices" element={<FeesPage initialTab="invoices" />} />
        <Route path="debtors" element={<DebtorsPage />} />

        <Route path="record-payment" element={<RecordPaymentPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="receipts" element={<ReceiptsPage />} />
        <Route path="reconciliation" element={<ReconciliationPage />} />

        <Route path="wallet" element={<OwnerWalletPage initialTab="overview" />} />
        <Route path="bank-accounts" element={<OwnerWalletPage key="bank" initialTab="bank" />} />
        <Route path="withdrawals" element={<OwnerWalletPage key="wd" initialTab="withdrawals" />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="stock" element={<StockPage />} />

        <Route path="books" element={<BooksPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="audit-log" element={<AuditTrailPage />} />
        <Route path="notices" element={<NoticesPage />} />
        <Route path="messages" element={<StaffMessagesPage />} />
        <Route path="support" element={<SupportCenter />} />
        <Route path="clock-in" element={<ClockInPage />} />
        <Route path="my-pay" element={<MyPayPage />} />
        <Route path="notifications" element={<NotificationsCenter />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>
    </Routes>
  );
}
